/**
 * Phase 3 reminders — pure helpers shared by the deal card, the calendar and the Notifications KPI tiles.
 * No React, no network: everything here is testable with plain Jest.
 *
 * A reminder: { id, deal_id, deal_id_number, user_id, due_at (ISO, UTC), note, status }.
 * "Today" is taken in the viewer's local calendar day (the CRM is used from New York).
 */

const MS_DAY = 86400000;

export const parseDue = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** YYYY-MM-DD of a Date in local time. */
export const dayKey = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** overdue | today | upcoming — only pending reminders are bucketed; done/dismissed → null. */
export const bucketOf = (reminder, now = new Date()) => {
  if (!reminder || reminder.status !== 'pending') return null;
  const due = parseDue(reminder.due_at);
  if (!due) return null;
  const today0 = startOfDay(now);
  const tomorrow0 = new Date(today0.getTime() + MS_DAY);
  if (due < today0) return 'overdue';
  if (due < tomorrow0) return 'today';
  return 'upcoming';
};

/** KPI counts for the Notifications page: unread (from notifications) + overdue / today / upcoming (pending reminders). */
export const kpiCounts = (reminders = [], notifications = [], now = new Date()) => {
  const out = { unread: 0, overdue: 0, today: 0, upcoming: 0 };
  for (const n of notifications) if (n && !n.read) out.unread += 1;
  for (const r of reminders) {
    const b = bucketOf(r, now);
    if (b) out[b] += 1;
  }
  return out;
};

/** Days overdue (positive) or until due (negative), whole local days. */
export const daysFromToday = (reminder, now = new Date()) => {
  const due = parseDue(reminder?.due_at);
  if (!due) return null;
  return Math.round((startOfDay(now) - startOfDay(due)) / MS_DAY);
};

/** { 'YYYY-MM-DD': [reminders sorted by due_at] } for the month grid. */
export const groupByDay = (reminders = []) => {
  const out = {};
  for (const r of reminders) {
    const due = parseDue(r?.due_at);
    if (!due) continue;
    const k = dayKey(due);
    (out[k] = out[k] || []).push(r);
  }
  for (const k of Object.keys(out)) out[k].sort((a, b) => (a.due_at < b.due_at ? -1 : a.due_at > b.due_at ? 1 : 0));
  return out;
};

/** Sorted list view: overdue first (oldest first), then today, then upcoming. Done/dismissed last. */
export const sortForList = (reminders = [], now = new Date()) => {
  const rank = { overdue: 0, today: 1, upcoming: 2 };
  return [...reminders].sort((a, b) => {
    const ra = rank[bucketOf(a, now)] ?? 3;
    const rb = rank[bucketOf(b, now)] ?? 3;
    if (ra !== rb) return ra - rb;
    return a.due_at < b.due_at ? -1 : a.due_at > b.due_at ? 1 : 0;
  });
};

/** Month window [first day 00:00, first day of next month 00:00) as ISO strings for the API. */
export const monthRange = (year, monthIndex) => {
  const from = new Date(year, monthIndex, 1);
  const to = new Date(year, monthIndex + 1, 1);
  return { from: from.toISOString(), to: new Date(to.getTime() - 1).toISOString() };
};

/** Combine a YYYY-MM-DD date and HH:MM time (local) into an ISO string; null when either is missing/invalid. */
export const toDueIso = (dateStr, timeStr = '09:00') => {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).split('-').map(Number);
  const [hh, mm] = String(timeStr || '09:00').split(':').map(Number);
  if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null;
  const dt = new Date(y, m - 1, d, hh, mm, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt.toISOString();
};

export const dealPath = (reminder) => (reminder?.deal_id ? `/deals/${reminder.deal_id}` : null);

export const fmtDue = (iso) => {
  const d = parseDue(iso);
  if (!d) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};
